import { bindOpenPencilTestImport } from '#react/app/document/test-import'
import { waitForPageRenderSettled } from '#react/app/document/fig'
import {
  refreshPageLoadingProgress,
  runColdPageSwitchWithProgress,
  setPageLoadingVisible
} from '#react/app/document/page-loading/controller'
import {
  pageLoadingLabels,
  pageLoadingProgressDetail
} from '#react/app/document/page-loading/labels'
import {
  emptyPageLoadingProgress,
  type PageLoadingProgress
} from '#react/app/document/page-loading/types'
import { appPreferences } from '#react/app/settings/preferences'
import { createCanvasPaneRegistry, type CanvasPaneRegistry } from '#react/editor/panes/registry'
import type { CanvasSplitNode, SplitDirection } from '#react/editor/panes/split-tree'
import {
  bootstrapHostedComponents,
  ensureHostedLibraries,
  hydrateHostedInstances
} from '#react/hosted-components'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode
} from 'react'

import { IS_BROWSER, IS_TAURI } from '@open-pencil/core/constants'
import {
  createEditor,
  createDefaultEditorState,
  type Editor,
  type EditorState
} from '@open-pencil/core/editor'
import '#react/app/editor/fonts'
import { SceneGraph } from '@open-pencil/scene-graph'
import { flushSync } from 'react-dom'

export type AppEditorState = EditorState & {
  loading: boolean
  /** Page-progress overlay (`2/5 封面`). */
  pageLoading: PageLoadingProgress
  showUI: boolean
  showRulers: boolean
  showRemoteCursors: boolean
  autosaveEnabled: boolean
  activeRibbonTab: 'panels' | 'code' | 'ai'
  panelMode: 'layers' | 'design'
  actionToast: string | null
  mobileDrawerSnap: 'closed' | 'half' | 'full'
  numberFieldFocused: boolean
  renameNodeId: string | null
  documentVersion: string
  documentFigURL: string
  documentKey: string
  historyPreviewId: string | null
}

export type EditorStore = Editor & {
  state: AppEditorState
  panes: CanvasPaneRegistry
  splitTree: CanvasSplitNode
  activePaneId: string
  visiblePaneCount: number
  getPaneRenderState: CanvasPaneRegistry['getPaneRenderState']
  setActivePane: CanvasPaneRegistry['setActivePane']
  splitPane: (
    paneId: string,
    direction: SplitDirection
  ) => ReturnType<CanvasPaneRegistry['splitPane']>
  closePane: CanvasPaneRegistry['closePane']
  resizePane: CanvasPaneRegistry['resizePane']
  setSplitSizes: CanvasPaneRegistry['setSplitSizes']
  subscribe: (onStoreChange: () => void) => () => void
  notify: () => void
  /** Coalesce React updates to one microtask (page switch / graph bursts). */
  scheduleNotify: () => void
  setShowUI: (value: boolean) => void
  setLoading: (value: boolean) => void
  prefetchRemainingLazyFigPages?: () => void
}

function createInitialAppEditorState(pageId: string): AppEditorState {
  const snapping = appPreferences.get().editing.snapping
  return {
    ...createDefaultEditorState(pageId),
    snappingPreferences: { ...snapping },
    loading: false,
    pageLoading: emptyPageLoadingProgress(),
    showUI: true,
    showRulers: true,
    showRemoteCursors: true,
    autosaveEnabled: false,
    activeRibbonTab: 'panels',
    panelMode: 'design',
    actionToast: null,
    mobileDrawerSnap: 'closed',
    numberFieldFocused: false,
    renameNodeId: null,
    documentVersion: '',
    documentFigURL: '',
    documentKey: '',
    historyPreviewId: null
  }
}

export function createEditorStore(initialGraph?: SceneGraph): EditorStore {
  const graph = initialGraph ?? new SceneGraph()
  const state = createInitialAppEditorState(graph.getPages()[0].id)
  const viewportSize = { width: 0, height: 0 }
  const editor = createEditor({
    graph,
    state,
    skipInitialGraphSetup: !!initialGraph,
    getViewportSize: () =>
      viewportSize.width > 0 && viewportSize.height > 0
        ? viewportSize
        : {
            width: IS_BROWSER ? window.innerWidth : 1920,
            height: IS_BROWSER ? window.innerHeight : 1080
          }
  })
  if (initialGraph) editor.subscribeToGraph()
  bootstrapHostedComponents()
  ensureHostedLibraries(editor.graph)
  hydrateHostedInstances(editor)
  editor.onEditorEvent('graph:replaced', (graph) => {
    // Hosted migrations may updateNode across the document; keep graph listeners
    // quiet so FIG open does not pay per-node renderer/Yjs work on the swap.
    const run = graph.runSilentMutations?.bind(graph) ?? ((action: () => void) => action())
    run(() => {
      ensureHostedLibraries(graph)
      hydrateHostedInstances(editor)
    })
  })

  const listeners = new Set<() => void>()
  let notifyQueued = false
  const flushNotify = () => {
    notifyQueued = false
    for (const listener of listeners) listener()
  }
  /**
   * Sync notify for flushSync / overlay show-hide. Cancels a pending coalesced
   * flush so we do not double-render after scheduleNotify + notify().
   */
  const notify = () => {
    notifyQueued = false
    for (const listener of listeners) listener()
  }
  /** Coalesce selection/page/viewport/render into one React update per turn. */
  const scheduleNotify = () => {
    if (notifyQueued) return
    notifyQueued = true
    queueMicrotask(() => {
      if (!notifyQueued) return
      flushNotify()
    })
  }
  let loadingDepth = 0
  const setLoading = (value: boolean) => {
    if (value) loadingDepth += 1
    else loadingDepth = Math.max(0, loadingDepth - 1)
    const next = loadingDepth > 0
    if (state.loading === next) return
    state.loading = next
    notify()
  }
  const panes = createCanvasPaneRegistry(state, scheduleNotify)

  editor.onEditorEvent('render:requested', scheduleNotify)
  // Coalesce: commitPageSwitch clears selection, emits page:changed, may change
  // viewport, then requestRender — one React pass instead of three shell rebuilds.
  editor.onEditorEvent('selection:changed', scheduleNotify)
  editor.onEditorEvent('tool:changed', scheduleNotify)
  editor.onEditorEvent('page:changed', (pageId) => {
    scheduleNotify()
    const documentKey = state.documentKey?.trim()
    if (!documentKey || !pageId) return
    void import('#react/app/document/page-frequency').then(({ pageFrequencyIdForGraphPage, recordPageVisit }) => {
      const visitId = pageFrequencyIdForGraphPage(editor.graph, pageId)
      if (visitId) void recordPageVisit(documentKey, visitId)
    })
  })
  editor.onEditorEvent('viewport:changed', scheduleNotify)

  const baseSwitchPage = editor.switchPage.bind(editor)
  const basePrefetchRemainingLazyFigPages = editor.prefetchRemainingLazyFigPages?.bind(editor)

  const store = Object.assign(editor, {
    state,
    panes,
    get splitTree() {
      return panes.splitTree
    },
    get activePaneId() {
      return panes.activePaneId
    },
    get visiblePaneCount() {
      return panes.visiblePaneCount
    },
    getPaneRenderState: panes.getPaneRenderState,
    setActivePane: panes.setActivePane,
    splitPane: panes.splitPane,
    closePane: panes.closePane,
    resizePane(paneId: string, width: number, height: number) {
      panes.resizePane(paneId, width, height)
      if (paneId === panes.activePaneId) {
        viewportSize.width = width
        viewportSize.height = height
      }
    },
    setSplitSizes: panes.setSplitSizes,
    subscribe(onStoreChange: () => void) {
      listeners.add(onStoreChange)
      return () => listeners.delete(onStoreChange)
    },
    notify,
    scheduleNotify,
    setShowUI(value: boolean) {
      state.showUI = value
      scheduleNotify()
    },
    setLoading,
    prefetchRemainingLazyFigPages: () => {
      basePrefetchRemainingLazyFigPages?.()
    }
  }) satisfies EditorStore

  store.switchPage = async (pageId, options) => {
    if (pageId === store.state.currentPageId) {
      await baseSwitchPage(pageId, options)
      return
    }
    // Document open already owns the overlay — don't nest or clear it early.
    if (store.state.loading || store.state.pageLoading.visible) {
      await baseSwitchPage(pageId, options)
      return
    }

    // Cold and warm switches both show the canvas overlay until switch + paint
    // settle. Force it into the DOM before commit.
    flushSync(() => {
      setPageLoadingVisible(store, true, pageLoadingLabels.preparingNodes, {
        blockShell: false
      })
    })
    await runColdPageSwitchWithProgress(store, pageId, async () => {
      await baseSwitchPage(pageId, {
        ...options,
        onProgress: (progress) => {
          options?.onProgress?.(progress)
          refreshPageLoadingProgress(store, pageLoadingProgressDetail(progress))
        }
      })
      await waitForPageRenderSettled(store)
    })
  }

  return store
}

const EditorStoreContext = createContext<EditorStore | null>(null)

export function EditorStoreProvider({
  store,
  children
}: {
  store: EditorStore
  children?: ReactNode
}) {
  useEffect(() => bindOpenPencilTestImport(store), [store])
  useEffect(() => {
    // Register this tab with the local MCP companion so Cursor/stdio tools can
    // reach the live document (React only started the process before).
    if (!IS_BROWSER) return
    if (!import.meta.env.DEV && !IS_TAURI) return
    let disconnect: (() => void) | undefined
    void import('#react/app/automation/bridge/server').then(({ connectAutomation }) => {
      disconnect = connectAutomation(() => store).disconnect
    })
    return () => disconnect?.()
  }, [store])
  // Page IDB snapshots are not restored on switch yet; the idle writer
  // structuredClone'd whole pages into IndexedDB and contended with cold/warm
  // switches (main-thread stalls / OOM). Re-enable only with a read path.
  return <EditorStoreContext.Provider value={store}>{children}</EditorStoreContext.Provider>
}

export function useOptionalEditorStore(): EditorStore | null {
  return useContext(EditorStoreContext)
}

/** Editor store without subscribing — for leaves that read via useSceneComputed / props. */
export function useEditorStoreApi(): EditorStore {
  const store = useContext(EditorStoreContext)
  if (!store) throw new Error('Editor store not provided')
  return store
}

export function useEditorStore(): EditorStore {
  const store = useEditorStoreApi()
  useSyncExternalStore(
    store.subscribe,
    () =>
      `${store.state.loading}:${store.state.pageLoading.visible}:${store.state.pageLoading.completed}/${store.state.pageLoading.total}:${store.state.pageLoading.detail ?? ''}:${store.state.showUI}:${store.state.sceneVersion}:${store.state.activeTool}:${store.state.editingTextId ?? ''}:${store.activePaneId}:${store.visiblePaneCount}:${store.state.mobileDrawerSnap}:${store.state.activeRibbonTab}:${store.state.panelMode}:${store.state.actionToast ?? ''}:${store.state.documentName}:${store.state.documentVersion}:${store.state.documentFigURL}:${store.state.documentKey}:${store.state.historyPreviewId ?? ''}:${store.state.zoom}:${store.state.currentPageId}:${[...store.state.selectedIds].join(',')}:${store.state.guides.selected?.guideId ?? ''}:${store.state.showRulers}:${store.state.showRemoteCursors}:${store.state.autosaveEnabled}:${store.state.snappingPreferences.geometry}:${store.state.snappingPreferences.objects}:${store.state.snappingPreferences.pixelGrid}:${store.renderer?.profiler.hudVisible ?? false}:${store.state.numberFieldFocused}:${store.state.renameNodeId ?? ''}`,
    () => 'ssr'
  )
  return store
}

export function useCreateEditorStore(initialGraph?: SceneGraph): EditorStore {
  const storeRef = useRef<EditorStore | null>(null)
  return useMemo(() => {
    storeRef.current ??= createEditorStore(initialGraph)
    return storeRef.current
  }, [initialGraph])
}
